import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ApiResponse } from '../../menu/types';
import { toCamelCase } from '../utils';
import type { InvoiceDeliveryResponse } from './invoiceDeliveryApi';
import { waitForInvoiceDeliveryResult } from './invoiceDeliveryApi';

export type SimpleOutboundClientOption = {
  id: number;
  name: string;
  /** CrmInvoiceEmail (finance); may contain multiple semicolon-separated addresses. */
  financeEmail?: string | null;
  /** CrmRelationEmail (regular); may contain multiple semicolon-separated addresses. */
  clientEmail?: string | null;
};

export type ComposeRecipient = {
  clientId: number;
  clientName: string;
  to: string;
  financeEmail: string;
  clientEmail: string;
};

export type SimpleOutboundPreview = {
  clientId: number;
  clientName: string;
  financeEmail: string;
  clientEmail: string;
  to: string;
  cc: string;
  bcc?: string;
  subject: string;
  message: string;
  isReply?: boolean;
  isReplyAll?: boolean;
  replyToHistoryId?: string;
  replyToSubject?: string;
  inReplyTo?: string;
  references?: string;
};

export type SimpleOutboundSendRequest = {
  clientId: number;
  to: string;
  cc: string;
  subject: string;
  message: string;
  files: File[];
  inReplyTo?: string;
  references?: string;
};

export const SIMPLE_OUTBOUND_API = '/umbraco/api/FinanceSimpleOutbound';

const SEND_SUCCESS_MESSAGE = 'Email was successfully sent';
const SEND_FAILURE_MESSAGE = 'Email was not sent. Please try again or contact support';

export const simpleOutboundApi = createApi({
  reducerPath: 'simpleOutboundApi',
  baseQuery,
  endpoints: (builder) => ({
    getSimpleOutboundClients: builder.query<SimpleOutboundClientOption[], void>({
      query: () => ({
        url: `${SIMPLE_OUTBOUND_API}/GetClients`,
      }),
      transformResponse: (response: ApiResponse<SimpleOutboundClientOption[]>) => {
        if ((response.Code ?? response.code) === 1) {
          const data = toCamelCase(response.data) as SimpleOutboundClientOption[];
          return Array.isArray(data) ? data : [];
        }

        throw new Error(extractErrorMessage(response, 'Failed to load clients.'));
      },
    }),
    getConfiguredFinanceBcc: builder.query<string, void>({
      query: () => ({
        url: `${SIMPLE_OUTBOUND_API}/GetConfiguredBcc`,
      }),
      transformResponse: (response: ApiResponse<{ bcc?: string }>) => {
        if ((response.Code ?? response.code) === 1) {
          const data = toCamelCase(response.data) as { bcc?: string };
          return (data?.bcc ?? '').trim();
        }

        throw new Error(extractErrorMessage(response, 'Failed to load BCC recipients.'));
      },
    }),
    prepareSimpleOutboundPreview: builder.mutation<SimpleOutboundPreview, number>({
      query: (clientId) => ({
        url: `${SIMPLE_OUTBOUND_API}/PreparePreview`,
        method: 'POST',
        body: { clientId },
      }),
      transformResponse: (response: ApiResponse<SimpleOutboundPreview>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as SimpleOutboundPreview;
        }

        throw new Error(extractErrorMessage(response, 'Request failed.'));
      },
    }),
    prepareSimpleOutboundReplyPreview: builder.mutation<
      SimpleOutboundPreview,
      { historyId: string; replyAll?: boolean }
    >({
      query: ({ historyId, replyAll }) => ({
        url: `${SIMPLE_OUTBOUND_API}/PrepareReplyPreview`,
        method: 'POST',
        body: { historyId, replyAll: Boolean(replyAll) },
      }),
      transformResponse: (response: ApiResponse<SimpleOutboundPreview>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as SimpleOutboundPreview;
        }

        throw new Error(extractErrorMessage(response, 'Failed to prepare reply preview.'));
      },
    }),
    sendSimpleOutbound: builder.mutation<InvoiceDeliveryResponse, SimpleOutboundSendRequest>({
      query: ({ clientId, to, cc, subject, message, files, inReplyTo, references }) => {
        const formData = new FormData();
        formData.append('clientId', String(clientId));
        formData.append('to', to);
        formData.append('cc', cc);
        formData.append('subject', subject);
        formData.append('message', message);
        if (inReplyTo) {
          formData.append('inReplyTo', inReplyTo);
        }
        if (references) {
          formData.append('references', references);
        }
        files.forEach((file) => formData.append('files', file));

        return {
          url: `${SIMPLE_OUTBOUND_API}/Send`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: ApiResponse<InvoiceDeliveryResponse>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as InvoiceDeliveryResponse;
        }

        throw new Error(extractErrorMessage(response, SEND_FAILURE_MESSAGE));
      },
    }),
    getSimpleOutboundStatus: builder.query<InvoiceDeliveryResponse, number>({
      query: (historyId) => ({
        url: `${SIMPLE_OUTBOUND_API}/SendStatus`,
        params: { historyId },
      }),
      transformResponse: (response: ApiResponse<InvoiceDeliveryResponse>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as InvoiceDeliveryResponse;
        }

        throw new Error(extractErrorMessage(response, 'Failed to get email send status.'));
      },
    }),
  }),
});

export const waitForSimpleOutboundResult = waitForInvoiceDeliveryResult;

export const {
  useGetSimpleOutboundClientsQuery,
  useGetConfiguredFinanceBccQuery,
  usePrepareSimpleOutboundPreviewMutation,
  usePrepareSimpleOutboundReplyPreviewMutation,
  useSendSimpleOutboundMutation,
  useLazyGetSimpleOutboundStatusQuery,
} = simpleOutboundApi;

export { SEND_SUCCESS_MESSAGE as SIMPLE_OUTBOUND_SEND_SUCCESS_MESSAGE };
